import { memo } from 'react';

import { Button } from '@/shadcn/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/shadcn/dialog';
import { Input } from '@/shadcn/input';
import { Label } from '@/shadcn/label';

import { TestId } from '@/types/testId';
import { useInput } from '@/utils/hooks';

type Props = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  submit: (groupName: string) => void;
  workflowName: string;
};

// The modal to add a new group to a workflow
export const AddGroupModal = memo(
  ({ isOpen, onOpenChange, submit, workflowName }: Props) => {
    const [groupName, updateGroupName] = useInput('');

    return (
      <Dialog onOpenChange={onOpenChange} open={isOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create a new group</DialogTitle>
            <DialogDescription>
              Add a new group to{' '}
              <strong className="font-semibold text-primary">
                {workflowName}
              </strong>
              .
            </DialogDescription>
          </DialogHeader>

          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor={TestId.WorkflowGroups.AddGroupModal.GroupNameInput}>
              Group Name
            </Label>
            <Input
              autoComplete="off"
              autoFocus
              className="bg-muted"
              data-testid={TestId.WorkflowGroups.AddGroupModal.GroupNameInput}
              id={TestId.WorkflowGroups.AddGroupModal.GroupNameInput}
              onChange={updateGroupName}
              placeholder="Group Name"
              type="text"
              value={groupName}
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button
                data-testId={TestId.WorkflowGroups.AddGroupModal.CancelButton}
                variant="outline"
              >
                Cancel
              </Button>
            </DialogClose>

            <Button
              data-testid={TestId.WorkflowGroups.AddGroupModal.SubmitButton}
              disabled={!groupName}
              onClick={() => submit(groupName)}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
);
